import { Modal } from 'components/modal/modal'
import { SUBSCRIPTION_PACKAGES } from 'constants/ai'
import React from 'react'
import { useSelector } from 'react-redux'
import { selectAiSubscriptionIsAdFreeOnly, selectAiSubscriptionType } from 'store/slices/subscription/subscription'
import { Button } from 'ui-kit/button'

import { Description, ModalContainer, Title } from './CancellationModal.styles'
import { CheckoutMetadata } from './Checkout'
import { ButtonContainer } from './PlanSwitchModal.styles'

interface PlanSwitchModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  metadata: CheckoutMetadata
}

/**
 * This function returns the text to display in the plan switch modal.
 * It maps the pricing period and plan type to the display name.
 * @param metadata - The metadata of the current plan.
 * @returns The text to display in the plan switch modal.
 */
const getToText = (metadata: CheckoutMetadata): string => {
  const { pricingPeriod, planType } = metadata

  // Early return for invalid data
  if (!pricingPeriod || !planType) {
    return 'Unknown Plan'
  }

  // Map pricing periods to display names
  const periodMap: Record<string, string> = {
    monthly: 'Monthly',
    yearly: 'Yearly',
    trial: 'Trial',
    canceled: 'Canceled',
  }

  // Map plan types to display names
  const planMap: Record<string, string> = {
    [SUBSCRIPTION_PACKAGES.PREMIUM]: 'Premium',
    [SUBSCRIPTION_PACKAGES.AD_FREE]: 'Ad-Free',
  }

  const period = periodMap[pricingPeriod] || 'Unknown'
  const plan = planMap[planType] || 'Unknown'

  return `${period} ${plan}`
}

/**
 * This function returns the text to display for the current plan.
 * It maps the billing period and plan type to the display name.
 * @param currentBillingPeriod - The current billing period from Redux state
 * @param isAdFreeOnly - Whether the current plan is ad-free only
 * @returns The text to display for the current plan.
 */
const getCurrentPlanText = (currentBillingPeriod: string | null, isAdFreeOnly: boolean | null): string => {
  // Early return for invalid data
  if (!currentBillingPeriod) {
    return 'Unknown Plan'
  }

  // Map billing periods to display names
  const periodMap: Record<string, string> = {
    monthly: 'Monthly',
    yearly: 'Yearly',
    trial: 'Trial',
    canceled: 'Canceled',
  }

  // Map plan types to display names
  const planMap: Record<string, string> = {
    [SUBSCRIPTION_PACKAGES.PREMIUM]: 'Premium',
    [SUBSCRIPTION_PACKAGES.AD_FREE]: 'Ad-Free',
  }

  const period = periodMap[currentBillingPeriod] || 'Unknown'
  const plan = isAdFreeOnly ? planMap[SUBSCRIPTION_PACKAGES.AD_FREE] : planMap[SUBSCRIPTION_PACKAGES.PREMIUM]

  return `${period} ${plan}`
}

export const PlanSwitchModal: React.FC<PlanSwitchModalProps> = ({ isOpen, onClose, onConfirm, metadata }) => {
  const currentBillingPeriod = useSelector(selectAiSubscriptionType)
  const isAdFreeOnly = useSelector(selectAiSubscriptionIsAdFreeOnly)

  // Get the current plan text
  const currentPlanText = getCurrentPlanText(currentBillingPeriod, isAdFreeOnly)
  // Get the to plan text
  const toPlanText = getToText(metadata)

  return (
    <Modal isOpen={isOpen} onClose={onClose} center fluid>
      <ModalContainer>
        <Title>Switch Subscription Plan</Title>
        <Description>
          Are you sure you want to switch from <b>{currentPlanText}</b> to <b>{toPlanText}</b> plan? Your billing will
          be updated accordingly and the changes will take effect immediately.
        </Description>
        <ButtonContainer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm}>
            Confirm Switch
          </Button>
        </ButtonContainer>
      </ModalContainer>
    </Modal>
  )
}
